/**
 * Customer retail listable helpers (G1).
 * Favorites / Purchases (guest + signed-in) intersect against the public Obchody
 * directory allowlist (same SoT as `/shops`). Never trust contacts / local LS alone.
 *
 * Unlistable = DISABLED surface_customer OR product_vending (mirrors backend helper).
 */

export const CUSTOMER_RETAIL_LISTABLE_BLOCK_KEYS = [
  'surface_customer',
  'product_vending',
] as const;

/** True when both retail listing blocks are entitled (not DISABLED). */
export function isCustomerRetailListableFromBlocks(input: {
  readonly surfaceCustomerEntitled: boolean;
  readonly productVendingEntitled: boolean;
}): boolean {
  return input.surfaceCustomerEntitled && input.productVendingEntitled;
}

export function tenantSlugListableKey(tenantCode: string, slug: string): string {
  return `${tenantCode.trim().toLowerCase()}|${slug.trim().toLowerCase()}`;
}

/**
 * Guest favourite row is listable when its salesPointId (or tenant+slug) appears
 * on the public customer directory (which already applies G1 entitlement SQL).
 */
export function isPendingFavoriteListableOnDirectory(
  item: {
    readonly salesPointId?: number;
    readonly tenantCode: string;
    readonly code?: string;
    readonly salesPointSlug?: string;
  },
  listableSalesPointIds: ReadonlySet<number>,
  listableTenantSlugKeys: ReadonlySet<string>,
): boolean {
  if (typeof item.salesPointId === 'number' && item.salesPointId > 0) {
    return listableSalesPointIds.has(item.salesPointId);
  }
  const slug = (item.salesPointSlug ?? item.code ?? '').trim();
  if (slug.length === 0) {
    return false;
  }
  return listableTenantSlugKeys.has(tenantSlugListableKey(item.tenantCode, slug));
}

/** Omit guest favourites that are not on the public listable directory set. */
export function filterPendingFavoritesByListableDirectory<
  T extends {
    readonly salesPointId?: number;
    readonly tenantCode: string;
    readonly code?: string;
    readonly salesPointSlug?: string;
  },
>(
  items: readonly T[],
  listableSalesPointIds: ReadonlySet<number>,
  listableTenantSlugKeys: ReadonlySet<string>,
): T[] {
  return items.filter((item) =>
    isPendingFavoriteListableOnDirectory(item, listableSalesPointIds, listableTenantSlugKeys),
  );
}

/**
 * Signed-in Favorites / Purchases — same Obchody directory allowlist as guest + `/shops`.
 * Defense-in-depth when contacts API still returns non-payReady rows (undeployed BE).
 */
export function isContactListableOnDirectory(
  item: {
    readonly salesPointId?: number;
    readonly tenantCode: string;
    readonly code?: string;
    readonly salesPointSlug?: string;
  },
  listableSalesPointIds: ReadonlySet<number>,
  listableTenantSlugKeys: ReadonlySet<string>,
): boolean {
  return isPendingFavoriteListableOnDirectory(
    item,
    listableSalesPointIds,
    listableTenantSlugKeys,
  );
}

/** Omit signed-in contacts that are not on the public listable directory set. */
export function filterContactsByListableDirectory<
  T extends {
    readonly salesPointId?: number;
    readonly tenantCode: string;
    readonly code?: string;
    readonly salesPointSlug?: string;
  },
>(
  items: readonly T[],
  listableSalesPointIds: ReadonlySet<number>,
  listableTenantSlugKeys: ReadonlySet<string>,
): T[] {
  return filterPendingFavoritesByListableDirectory(
    items,
    listableSalesPointIds,
    listableTenantSlugKeys,
  );
}

/**
 * Soft tenant-level prune (discovery seed only) — NOT Favorites display SoT.
 *
 * Guest Favorites must intersect against the public SP directory allowlist
 * (`filterPendingFavoritesByListableDirectory`). Tenant discovery alone can
 * still admit a non-Obchody / non-payReady SP of a tenant that was ever marked
 * listable — forbidden for Favorites / Purchases.
 *
 * Prefer returning `[]` when the directory allowlist is unavailable (fail closed).
 */
export function filterPendingFavoritesHidingKnownUnlistable<
  T extends { readonly tenantCode: string },
>(
  items: readonly T[],
  isTenantListable: (tenantCode: string) => boolean | null,
): T[] {
  return items.filter((item) => isTenantListable(item.tenantCode) === true);
}

/**
 * @deprecated Favorites must not open on discovery alone — use directory allowlist.
 * Always false (fail closed): unknown directory ⇒ not enterable.
 */
export function isPendingFavoriteOpenableWhenDirectoryUnknown(
  _tenantCode: string,
  _isTenantListable: (code: string) => boolean | null,
): boolean {
  void _tenantCode;
  void _isTenantListable;
  return false;
}

/**
 * Signed-in contacts: server already filters unlistable tenants.
 * Client omits rows that cannot be opened (empty / missing shop URL or tenantCode).
 * Nullish URL / tenantCode must not throw — partial API payloads used to crash Nákupy EB.
 */
export function filterOpenableSalesPointContacts<
  T extends {
    readonly customerShopUrl?: string | null;
    readonly tenantCode?: string | null;
  },
>(items: readonly T[]): T[] {
  return items.filter((item) => {
    const url = item.customerShopUrl;
    const tenantCode = item.tenantCode;
    return (
      typeof url === 'string' &&
      url.trim().length > 0 &&
      typeof tenantCode === 'string' &&
      tenantCode.trim().length > 0
    );
  });
}
